import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { get } from '@sama/micro-dash';
import { toolNameToShapeType } from '../../../cross-team-shared/annotation/shared/abstract-tool';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { ToolName } from '../../../cross-team-shared/tools-names';
import { ShapeType } from '../../../team-apis/otterdam/answers/shape-typings';
import { outputInfoStoreToken } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import {
  ClipboardAnnotationData,
  CopyPasteMeta,
  isShapeGroupForCopyPaste,
} from './copy-paste-types';

export class PasteValidationError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}

@Injectable()
export class PasteValidationService {
  private fusionManager = inject(FusionManager);
  private browserControlService = inject(BrowserControlService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);

  validate(clipboardAnnotations: ClipboardAnnotationData) {
    assert(
      this.validateAnnotationFormat(clipboardAnnotations),
      'Shapes in clipboard should be from object projects',
    );

    const shapes = clipboardAnnotations.shapes;
    this.validateShapeTypes(shapes);
    this.validateShapes(clipboardAnnotations.meta, shapes);
    this.validateKeylocations(clipboardAnnotations.meta, shapes);
  }

  async isPasteInvalid() {
    try {
      const clipboardText =
        await this.browserControlService.readClipboardText();
      const copiedText = JSON.parse(clipboardText) as ClipboardAnnotationData;
      this.validate(copiedText);
      return false;
    } catch (e) {
      return true;
    }
  }

  private validateAnnotationFormat(
    clipboardShapes: ClipboardAnnotationData,
  ): clipboardShapes is ClipboardAnnotationData {
    if (clipboardShapes.meta.dataType !== 'Annotation') {
      throw new PasteValidationError(
        `Projects using the annotation store only allow pasting shapes from projects with same settings`,
      );
    }

    for (const shapeOrGroup of clipboardShapes.shapes) {
      if (isShapeGroupForCopyPaste(shapeOrGroup)) {
        throw new PasteValidationError(
          `Can't paste shape group in projects using annotation store: ${shapeOrGroup}`,
        );
      }
    }
    return true;
  }

  private validateShapes(meta: CopyPasteMeta, shapes: ShapeState[]) {
    for (const shape of shapes) {
      const errorMessage = this.getContentConnector(
        shape.outputName,
      ).validateShape(shape, meta.isForSingleFrame);

      if (errorMessage) {
        throw new PasteValidationError(
          errorMessage || `Shape ${shape} is invalid`,
        );
      }
    }
  }

  private validateKeylocations(meta: CopyPasteMeta, shapes: ShapeState[]) {
    // When pasting for a single keyframe; the frame number is overwritten.
    if (meta.isForSingleFrame) {
      return;
    }

    const maxFrameNumber = this.taskWorkspaceStore.getMaxFrameNumber(
      this.outputInfoStore.state().output.name,
    );

    const outOfBoundShapes = shapes
      .filter((shape) =>
        shape.key_locations.some(
          (keyLocation) =>
            keyLocation.frame_number > maxFrameNumber ||
            keyLocation.frame_number < 0,
        ),
      )
      .map((shape) => shape.index);

    if (outOfBoundShapes.length) {
      throw new PasteValidationError(
        `Shape(s) ${outOfBoundShapes.join(
          ', ',
        )} has a key location frame outside the limits of the video.`,
      );
    }
  }

  private validateShapeTypes(shapes: ShapeState[]) {
    const shapeTypesByOutput: StrictRecord<string, ShapeType[]> = {};

    for (const shape of shapes) {
      const { outputName } = shape;
      assert(isDefined(outputName), 'outputName should be defined');

      let availableShapeTypes = shapeTypesByOutput[outputName];
      if (!availableShapeTypes) {
        const { outputInfoByName } = this.taskWorkspaceStore.state();
        const availableTools: ToolName[] | undefined = get(outputInfoByName, [
          outputName,
          'output',
          'meta',
          'available_tools',
        ]);

        if (!isDefined(availableTools)) {
          throw new PasteValidationError(
            `Output ${outputName} not found in project`,
          );
        }

        availableShapeTypes = availableTools.map(toolNameToShapeType);
        shapeTypesByOutput[outputName] = availableShapeTypes;
      }

      if (!availableShapeTypes.includes(shape.type)) {
        throw new PasteValidationError(
          `${shape.type} is not a valid shape type for output ${outputName}`,
        );
      }
    }
  }

  private getContentConnector(outputName?: string) {
    assert(
      isDefined(outputName),
      'OutputName should be defined for object projects',
    );
    return this.fusionManager.getContentConnector(outputName);
  }
}
